import { AlertTriangle, CheckCircle, Info } from "lucide-react";
import Modal from "./Modal";

type ConfirmDialogProps = {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: "danger" | "warning" | "info" | "success";
  hideCancel?: boolean;
};

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "danger",
  hideCancel = false,
}: ConfirmDialogProps) {
  const variants = {
    danger: {
      icon: "bg-red-100 text-red-600",
      iconNode: <AlertTriangle className="h-6 w-6" />,
      button: "bg-red-600 hover:bg-red-700 text-white",
    },
    warning: {
      icon: "bg-yellow-100 text-yellow-600",
      iconNode: <AlertTriangle className="h-6 w-6" />,
      button: "bg-yellow-600 hover:bg-yellow-700 text-white",
    },
    info: {
      icon: "bg-blue-100 text-blue-600",
      iconNode: <Info className="h-6 w-6" />,
      button: "bg-blue-600 hover:bg-blue-700 text-white",
    },
    success: {
      icon: "bg-green-100 text-green-600",
      iconNode: <CheckCircle className="h-6 w-6" />,
      button: "bg-green-600 hover:bg-green-700 text-white",
    },
  };

  const style = variants[variant];

  return (
    <Modal
      open={isOpen}
      onClose={onCancel ?? (() => {})}
      showCloseButton={false}
      closeOnBackdropClick={Boolean(onCancel)}
      bodyClassName=""
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div
            className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${style.icon}`}
          >
            {style.iconNode}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {title}
            </h3>
            <p className="text-sm text-gray-600 [overflow-wrap:anywhere]">{message}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3 rounded-b-2xl bg-gray-50 px-6 py-4">
        {onCancel && !hideCancel && (
          <button
            onClick={onCancel}
            className="min-h-[44px] rounded-lg border-2 border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {cancelText}
          </button>
        )}
        <button
          onClick={onConfirm}
          className={`min-h-[44px] rounded-lg px-6 py-2 text-sm font-semibold transition-colors ${style.button}`}
        >
          {confirmText}
        </button>
      </div>
    </Modal>
  );
}
